'use client';

import { Box } from '@wishket/design-system';
import {
  ChangeEvent,
  ComponentProps,
  KeyboardEvent,
  useEffect,
  useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { useDeviceCheck } from '@/shared/hooks';
import { getScrollbarStyle } from '@/shared/utils';

const DESKTOP_MAX_HEIGHT = 208;
const MOBILE_MAX_HEIGHT = 9999;

interface AutoExpandQuoteTextareaProps extends ComponentProps<'textarea'> {
  onSubmit?: () => void;
  needAutofocus?: boolean;
}

const AutoExpandQuoteTextarea = ({
  value,
  onChange,
  onKeyDown,
  onSubmit,
  className,
  style,
  needAutofocus = false,
  ...rest
}: AutoExpandQuoteTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { isDesktop } = useDeviceCheck();

  const resizeTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      const maxHeight = isDesktop ? DESKTOP_MAX_HEIGHT : MOBILE_MAX_HEIGHT;

      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  };

  const handleOnChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!onChange) return;

    onChange(e);
    resizeTextareaHeight();
  };

  const handlePressEnterOnly = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const isPressEnterOnly = e.key === 'Enter' && !e.shiftKey;

    if (isDesktop && isPressEnterOnly) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  useEffect(() => {
    if (textareaRef.current && needAutofocus) {
      textareaRef.current.focus();
    }
  }, [needAutofocus]);

  useEffect(() => {
    if (value && textareaRef.current) {
      resizeTextareaHeight();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Box className="relative flex w-full items-center">
      <textarea
        ref={textareaRef}
        className={twMerge(
          'peer h-auto min-h-[26px] w-full resize-none text-w-gray-600 outline-none typo-body1',
          getScrollbarStyle(),
          'placeholder:text-w-gray-300'
        )}
        rows={1}
        value={value}
        onChange={handleOnChange}
        onKeyDown={onKeyDown || handlePressEnterOnly}
        spellCheck={false}
        {...rest}
      />
      <div
        className={twMerge(
          'absolute left-0 top-0 h-full min-h-[26px] w-1 rounded-3xl',
          'bg-w-gray-200 peer-hover:bg-primary peer-focus:bg-primary peer-active:bg-primary',
          value && 'bg-primary'
        )}
      />
    </Box>
  );
};

export default AutoExpandQuoteTextarea;
